import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, PlaneTakeoff, Waves, Crosshair, X } from "lucide-react";
import { toast } from "sonner";
import { getFlights, getGoWeather } from "../../lib/api";
import WeatherCard from "./WeatherCard";
import StormBanner from "./StormBanner";
import FlightsBoard from "./FlightsBoard";
import AircraftMiniMap from "./AircraftMiniMap";
import { weatherIcon } from "./weatherMeta";

const WATCH_KEY = "go_watched_flight";

const HourlyStrip = ({ hourly }) => {
  if (!hourly || !hourly.length) return null;
  return (
    <div>
      <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-[#67799A]">Next hours</h4>
      <div className="go-scroll flex gap-2 overflow-x-auto pb-1" data-testid="weather-hourly">
        {hourly.map((h, i) => {
          const Icon = weatherIcon(h.icon);
          return (
            <div key={i} className="go-card flex min-w-[62px] flex-col items-center rounded-2xl px-2 py-3">
              <span className="text-[11px] font-semibold text-[#8194B2]">{i === 0 ? "Now" : h.hour}</span>
              <Icon size={20} color="#5B9BFF" className="my-1.5" strokeWidth={2} />
              <span className="font-head text-sm font-bold text-white">{h.temp_c != null ? `${h.temp_c}°` : "—"}</span>
              {h.precip_pct != null && <span className="mt-0.5 text-[10px] font-semibold text-[#5B9BFF]">{h.precip_pct}%</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MarineCard = ({ marine }) => {
  if (!marine || marine.wave_height_m == null) return null;
  return (
    <div className="go-card rounded-3xl p-5" data-testid="marine-card">
      <div className="mb-3 flex items-center gap-2">
        <Waves size={18} color="#22E0A1" strokeWidth={2.3} />
        <h4 className="font-head text-sm font-bold text-white">Beach &amp; Marine</h4>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[["Wave", `${marine.wave_height_m} m`], ["Swell", marine.swell_m != null ? `${marine.swell_m} m` : "—"], ["Period", marine.wave_period_s != null ? `${Math.round(marine.wave_period_s)} s` : "—"]].map(([l, v]) => (
          <div key={l} className="rounded-2xl bg-white/[0.04] py-2.5 text-center">
            <div className="font-head text-lg font-bold text-white">{v}</div>
            <div className="text-[11px] text-[#67799A]">{l}</div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-sm text-[#A9B8D2]">{marine.beach_note}</p>
    </div>
  );
};

const TravelScreen = ({ weather, notify }) => {
  const { data: flights, isLoading } = useQuery({ queryKey: ["flights"], queryFn: getFlights, refetchInterval: 60000 });
  const { data: goWeather } = useQuery({ queryKey: ["go-weather"], queryFn: getGoWeather, refetchInterval: 120000 });

  const [focus, setFocus] = React.useState(null);
  const [watched, setWatched] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(WATCH_KEY) || "null"); } catch { return null; }
  });
  const mapRef = React.useRef(null);

  const current = goWeather?.current || weather;

  const persistWatch = (w) => {
    setWatched(w);
    try { w ? localStorage.setItem(WATCH_KEY, JSON.stringify(w)) : localStorage.removeItem(WATCH_KEY); } catch { /* noop */ }
    toast[w ? "success" : "message"](w ? `Watching ${w.flight}` : "Stopped watching flight");
  };

  const handleTrack = (f) => {
    const craft = flights?.airspace?.aircraft || [];
    const key = (f.flight || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    const hit = craft.find((a) => {
      const c = (a.flight || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      return c && key && (c.includes(key) || key.includes(c));
    });
    mapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (hit) { setFocus(hit); toast.success(`Tracking ${f.flight} in the airspace`); }
    else { setFocus(null); toast.message(`${f.flight} isn't in local airspace yet`, { description: "Showing all nearby traffic." }); }
  };

  // My-flight status change detection
  React.useEffect(() => {
    if (!watched || !flights?.available) return;
    const rows = watched.kind === "arrivals" ? flights.arrivals : flights.departures;
    const cur = (rows || []).find((r) => r.flight === watched.flight);
    if (cur && cur.status && cur.status !== watched.status) {
      const msg = `${watched.flight} is now ${cur.status}`;
      toast.info(msg, { description: `${watched.kind === "arrivals" ? "From" : "To"} ${watched.other}` });
      notify && notify(`Flight ${watched.flight}`, msg);
      const upd = { ...watched, status: cur.status };
      setWatched(upd);
      try { localStorage.setItem(WATCH_KEY, JSON.stringify(upd)); } catch { /* noop */ }
    }
  }, [flights, watched, notify]);

  return (
    <div className="go-scroll min-h-full overflow-y-auto px-5 pb-28 pt-[max(env(safe-area-inset-top),16px)]">
      <header className="go-fade-up mb-5">
        <h1 className="font-head text-2xl font-extrabold text-white">Weather &amp; Travel</h1>
        <p className="mt-1 text-sm text-[#8194B2]">Live conditions and the Grantley Adams flight board.</p>
      </header>

      <div className="space-y-4">
        <StormBanner status={current?.storm_status} summary={current?.storm_summary} />
        <WeatherCard w={current} />
        <HourlyStrip hourly={goWeather?.hourly} />
        <MarineCard marine={goWeather?.marine} />
      </div>

      <div className="mb-3 mt-7 flex items-center gap-2" ref={mapRef}>
        <PlaneTakeoff size={18} color="#5B9BFF" strokeWidth={2.4} />
        <h3 className="font-head text-sm font-bold uppercase tracking-wide text-[#67799A]">Grantley Adams · BGI</h3>
      </div>

      {isLoading || !flights ? (
        <div className="flex items-center justify-center py-16 text-[#67799A]"><Loader2 className="animate-spin" /></div>
      ) : flights.available ? (
        <div className="space-y-4">
          {flights.airspace && <AircraftMiniMap airspace={flights.airspace} focus={focus} />}
          {watched && (
            <div className="go-card flex items-center gap-3 rounded-2xl px-4 py-3" data-testid="watched-flight">
              <Crosshair size={18} color="#FFC726" strokeWidth={2.4} />
              <div className="min-w-0 flex-1">
                <div className="font-head text-sm font-bold text-white">Watching {watched.flight}</div>
                <div className="truncate text-xs text-[#8194B2]">{watched.kind === "arrivals" ? "From" : "To"} {watched.other} · {watched.status}</div>
              </div>
              <button onClick={() => persistWatch(null)} className="rounded-full p-1.5 active:scale-90" data-testid="unwatch-flight"><X size={16} color="#67799A" /></button>
            </div>
          )}
          <FlightsBoard flights={flights} onTrackFlight={handleTrack} watched={watched} onWatch={persistWatch} />
        </div>
      ) : (
        <div className="go-card rounded-3xl px-4 py-8 text-center text-sm text-[#67799A]" data-testid="flights-unavailable">
          {flights.note || "Live flight feed is warming up — check back shortly."}
        </div>
      )}
    </div>
  );
};

export default TravelScreen;
